import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, CameraOff, Keyboard } from "lucide-react";

interface QRScannerProps {
  onScan: (reference: string) => void;
}

export function QRScanner({ onScan }: QRScannerProps) {
  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [showManualInput, setShowManualInput] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerId = "qr-reader";

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    setError(null);
    setScanning(true); // Set scanning true FIRST to render the container
    
    // Wait for DOM to update
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const html5QrCode = new Html5Qrcode(containerId);
      scannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          onScan(decodedText);
          stopScanner();
        },
        () => {} // Ignore errors during scanning
      );
    } catch (err: any) {
      console.error("Scanner error:", err);
      setError("Unable to access camera. Please check permissions or use manual entry.");
      setScanning(false);
      setShowManualInput(true);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
      } catch (e) {
        // Ignore stop errors
      }
    }
    setScanning(false);
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualInput.trim()) {
      onScan(manualInput.trim());
      setManualInput("");
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        {!scanning && !showManualInput && (
          <div className="text-center space-y-4">
            {/* Clickable camera area - smaller and interactive */}
            <button
              onClick={startScanner}
              className="mx-auto w-48 h-48 bg-muted rounded-xl flex flex-col items-center justify-center gap-3 hover:bg-muted/80 transition-colors cursor-pointer border-2 border-dashed border-muted-foreground/30 hover:border-primary/50"
            >
              <div className="p-4 bg-primary/10 rounded-full">
                <Camera className="h-10 w-10 text-primary" />
              </div>
              <span className="text-sm font-medium text-muted-foreground">
                Tap to scan QR code
              </span>
            </button>
            
            <Button 
              variant="outline" 
              onClick={() => setShowManualInput(true)}
              className="w-full"
            >
              <Keyboard className="h-4 w-4 mr-2" />
              Enter Code Manually
            </Button>
          </div>
        )}

        {scanning && (
          <div className="space-y-4">
            <div 
              id={containerId} 
              className="w-full max-w-sm mx-auto rounded-lg overflow-hidden"
              style={{ minHeight: '280px' }}
            />
            <Button 
              variant="outline" 
              onClick={stopScanner}
              className="w-full"
            >
              <CameraOff className="h-4 w-4 mr-2" />
              Stop Scanner
            </Button>
          </div>
        )}

        {!scanning && showManualInput && (
          <div className="space-y-4">
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            <form onSubmit={handleManualSubmit} className="space-y-3">
              <Input
                placeholder="Enter ticket reference (e.g., ABC123)"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value.toUpperCase())}
                className="text-center text-lg tracking-widest"
                maxLength={6}
              />
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" disabled={!manualInput.trim()}>
                  Check Ticket
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => {
                    setShowManualInput(false);
                    setError(null);
                  }}
                >
                  Back
                </Button>
              </div>
            </form>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
